import { DEFAULT_SETTINGS, type SettingsResponse, type UserSettings } from '@parts/shared';
import { getJson, putJson } from './api';

/**
 * Личные настройки пользователя: тема, ширины столбцов, развёрнутые запросы, фильтры.
 * Хранятся на сервере для текущей роли, в браузере держится копия для мгновенного доступа.
 * Всё, кроме темы, лежит в словаре grids под своим ключом — сервер хранит его как есть.
 */
const THEME_KEY = 'parts.theme';

let current: UserSettings = { ...DEFAULT_SETTINGS, grids: {} };
let title = '';
const listeners = new Set<() => void>();
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function notify(): void {
  listeners.forEach((l) => l());
}

export function subscribeSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSettings(): UserSettings {
  return current;
}

export function getTitle(): string {
  return title;
}

/** Загрузка настроек. Приложение ждёт её перед первым показом — таблицы сразу открываются с сохранёнными размерами. */
export async function loadSettings(): Promise<void> {
  try {
    const res = await getJson<SettingsResponse>('/api/settings');
    current = { theme: res.settings.theme, grids: res.settings.grids ?? {} };
    title = res.title;
    if (title) document.title = title;
    applyTheme(current.theme);
    notify();
  } catch {
    // Сервер недоступен — тема из прошлого раза, остальное по умолчанию
    try {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved === 'dark' || saved === 'light') current = { ...current, theme: saved };
    } catch {
      /* нет доступа к хранилищу браузера */
    }
  }
}

export function setTitle(value: string): void {
  title = value;
  notify();
}

/** Запоминает тему в браузере: при следующем открытии страница сразу появится в ней. */
export function applyTheme(theme: 'light' | 'dark'): void {
  document.documentElement.setAttribute('data-mantine-color-scheme', theme);
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* браузер не дал сохранить — не страшно */
  }
}

export async function setTheme(theme: 'light' | 'dark'): Promise<void> {
  current = { ...current, theme };
  applyTheme(theme);
  notify();
  await putJson('/api/settings', { theme });
}

/** Сохранённое значение по ключу (ширины столбцов, развёрнутые строки, фильтры). */
export function getPref<T>(key: string, fallback: T): T {
  const v = current.grids[key];
  return v === undefined || v === null ? fallback : (v as T);
}

/** Сохранить значение. На сервер уходит пачкой — частые изменения (перетаскивание ширины) не грузят его. */
export function setPref(key: string, value: unknown): void {
  current = { ...current, grids: { ...current.grids, [key]: value } };
  notify();
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    void putJson('/api/settings', { grids: current.grids }).catch(() => undefined);
  }, 700);
}

/** Настройки таблицы для Tabulator (ширины и видимость столбцов). */
export function getGridSettings(key: string): unknown {
  return current.grids[key] ?? false;
}

export function saveGridSettings(key: string, value: unknown): void {
  setPref(key, value);
}

/* ---------- Масштаб таблиц и свёрнутая боковая панель ---------- */

export const ZOOM_MIN = 10;
export const ZOOM_MAX = 200;
export const ZOOM_STEP = 5;
const ZOOM_KEY = 'ui:tableZoom';
const SIDEBAR_KEY = 'ui:sidebarCollapsed';

/** Масштаб таблиц, % (100 — обычный). */
export function getZoom(): number {
  const v = Number(getPref<number>(ZOOM_KEY, 100));
  return Number.isFinite(v) ? Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v)) : 100;
}

export function setZoom(value: number): void {
  const snapped = Math.round(value / ZOOM_STEP) * ZOOM_STEP;
  setPref(ZOOM_KEY, Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, snapped)));
}

export function isSidebarCollapsed(): boolean {
  return getPref<boolean>(SIDEBAR_KEY, false) === true;
}

export function setSidebarCollapsed(value: boolean): void {
  setPref(SIDEBAR_KEY, value);
}
